import { RefreshCw } from "lucide-react";
import { Badge } from "../../../shared/components/ui/badge";
import type { Submission, SubmissionAsset } from "../../content-editing/hooks/useSubmissions";
import { EncodingStatusBadge } from "./EncodingStatusBadge";

interface AdminActionsPanelProps {
  readonly submission: Submission;
  readonly onApprove: () => void;
  readonly onReject: () => void;
  readonly onRefresh?: () => void;
  readonly isRefreshing?: boolean;
  readonly onTriggerEncoding?: () => void;
  readonly isEncoding?: boolean;
}

function getStatusBadgeVariant(status: string): "draft" | "pending" | "approved" | "rejected" | "published" {
  if (status === "PENDING") return "pending";
  if (status === "APPROVED") return "approved";
  if (status === "REJECTED") return "rejected";
  if (status === "PUBLISHED") return "published";
  return "draft";
}

export function AdminActionsPanel({
  submission,
  onApprove,
  onReject,
  onRefresh,
  isRefreshing,
  onTriggerEncoding,
  isEncoding,
}: AdminActionsPanelProps) {
  const canTakeAction = submission.status === "PENDING";
  const canEncodeAssets = submission.status === "APPROVED" || submission.status === "PUBLISHED";

  // Check if any assets need encoding (UPLOADED but not yet ENCODING or READY)
  const hasUploadedAssets = submission.assets?.some((asset) => asset.encodingStatus === "UPLOADED");

  return (
    <div className="bg-neutral-900/0 border-l border-neutral-800 p-6 pl-12 sticky top-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Admin Actions</h3>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="p-2 text-neutral-400 hover:text-white hover:bg-neutral-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh encoding status"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
          </button>
        )}
      </div>

      {/* Status Display */}
      <div className="mb-6">
        <p className="text-sm text-neutral-500 mb-2">Current Status</p>
        <Badge variant={getStatusBadgeVariant(submission.status)}>{submission.status}</Badge>
      </div>

      {/* Submission Info */}
      <div className="mb-6 space-y-2 text-sm">
        <div>
          <span className="text-neutral-500">Submitted by:</span>
          <p className="text-white">Indie Films Co</p>
        </div>
        <div>
          <span className="text-neutral-500">Submitted on:</span>
          <p className="text-white">Oct 3, 2025</p>
        </div>
      </div>

      {/* Action Buttons */}
      {canTakeAction ? (
        <div className="space-y-3">
          <button
            onClick={onApprove}
            className="w-full px-4 py-3 text-sm bg-green-600/10 border border-green-600 text-green-600 font-semibold rounded-lg hover:bg-green-700/100 hover:text-white transition-colors"
          >
            ✓ Approve Submission
          </button>
          <button
            onClick={onReject}
            className="w-full px-4 py-3 text-sm bg-red-600/10 border border-red-600 text-red-600 font-semibold rounded-lg hover:bg-red-700/100 hover:text-white transition-colors"
          >
            ✕ Reject Submission
          </button>
        </div>
      ) : (
        <>
          {/* Publish Button (only for approved submissions with uploaded videos) */}
          {canEncodeAssets && hasUploadedAssets && onTriggerEncoding && (
            <div className="mb-4">
              <button
                onClick={onTriggerEncoding}
                disabled={isEncoding}
                className="w-full px-4 py-3 text-sm bg-blue-600/10 border border-blue-600 text-blue-600 font-semibold rounded-lg hover:bg-blue-700/100 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isEncoding ? "Publishing..." : "Publish"}
              </button>
              <p className="text-xs text-neutral-500 mt-2">Encodes videos and publishes when ready</p>
            </div>
          )}

          <div className="bg-neutral-800/50 border border-neutral-700 rounded-lg p-4 text-center">
            <p className="text-sm text-neutral-400">This submission has already been reviewed.</p>
          </div>
        </>
      )}

      {/* Review History Placeholder */}
      {submission.reviewedAt && (
        <div className="mt-6 pt-6 border-t border-neutral-800">
          <p className="text-sm text-neutral-500 mb-2">Review History</p>
          <div className="text-xs text-neutral-400 space-y-1">
            <p>Reviewed on: {new Date(submission.reviewedAt).toLocaleDateString()}</p>
            {submission.reviewNotes && <p className="mt-2 text-sm text-white">{submission.reviewNotes}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export function VideoPlayerPlaceholder() {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-lg overflow-hidden">
      <div className="aspect-video bg-neutral-800 flex items-center justify-center">
        <div className="text-center space-y-3">
          <svg className="w-16 h-16 text-neutral-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
            />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-white font-semibold">Video Preview</p>
            <p className="text-sm text-neutral-500 mt-1">HLS player in Phase 2</p>
          </div>
        </div>
      </div>
      <div className="p-4 border-t border-neutral-800">
        <div className="flex items-center justify-between text-sm">
          <span className="text-neutral-500">the-silent-echo-master.mp4</span>
          <span className="text-neutral-400">4.2 GB • Encoding: PENDING</span>
        </div>
      </div>
    </div>
  );
}

interface AssetsListProps {
  readonly assets: SubmissionAsset[];
}

export function AssetsList({ assets }: AssetsListProps) {
  if (!assets || assets.length === 0) return null;

  return (
    <div className="border-t border-neutral-800 pt-12">
      <h2 className="text-lg font-semibold text-white mb-4">Uploaded Assets</h2>
      <div className="space-y-3">
        {assets.map((asset) => (
          <div key={asset.id} className="bg-neutral-800/30 border border-neutral-700/50 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="text-white font-medium text-sm uppercase tracking-wide">{asset.kind}</span>
                {asset.rawFileName && <div className="text-xs text-neutral-400 mt-1">{asset.rawFileName}</div>}
              </div>
              <EncodingStatusBadge
                status={asset.encodingStatus as "PENDING" | "UPLOADING" | "ENCODING" | "READY" | "ERRORED"}
                error={asset.encodingError}
              />
            </div>

            {/* Additional asset details */}
            <div className="flex items-center gap-4 text-xs text-neutral-500">
              {asset.rawFileSize && (
                <span>Size: {(Number.parseInt(asset.rawFileSize) / (1024 * 1024 * 1024)).toFixed(2)} GB</span>
              )}
              {asset.durationSec && (
                <span>
                  Duration: {Math.floor(asset.durationSec / 60)}m {asset.durationSec % 60}s
                </span>
              )}
              {asset.hlsUrl && <span className="text-green-500">✓ HLS Ready</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
